import { X } from "lucide-react";
import { useMemo, useState } from "react";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { urlDeLaFotoDeLaFila, type PlantacionDeInforme } from "@/lib/informes";
import { cn } from "@/lib/utils";

import { RotuloDeGrupo } from "./CamposDeConstruccion";
import { GrupoDeDatos, GrupoDeIdentificacion, PlantasTotales } from "./CamposDePlantacion";
import { mismosCampos } from "./DialogoDeEdicionDeFila";
import { FotosDeLaFila } from "./FotosDeLaFila";
import {
  formularioDesdePlantacion,
  nombreDeLaPlantacion,
  type FormularioDePlantacion,
} from "./plantaciones";

/** Lo que el modal entrega al guardar. */
export interface GuardadoDeLaPlantacion {
  formulario: FormularioDePlantacion;
  validada: boolean;
}

interface DialogoDePlantacionProps {
  /** La fila que se abre, o `null` con el modal cerrado. */
  fila: PlantacionDeInforme | null;
  /** Todas las filas del informe: de ahí salen las sugerencias de especie. */
  filas: PlantacionDeInforme[];
  informeId: number;
  ocupada: boolean;
  onGuardar: (fila: PlantacionDeInforme, guardado: GuardadoDeLaPlantacion) => void;
  onQuitar: (fila: PlantacionDeInforme) => void;
  onCerrar: () => void;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
}

/**
 * El modal "Editar plantación": los campos, las plantas totales que saldrían
 * y las fotos de ESTE cuartel.
 *
 * SE REMONTA POR FILA (`key`): el estado de lo que se está escribiendo nace de
 * la fila que se abre y muere con el modal, así que abrir otra nunca hereda un
 * borrador ajeno.
 */
export function DialogoDePlantacion(props: DialogoDePlantacionProps) {
  return props.fila === null ? null : <Contenido key={props.fila.id} {...props} fila={props.fila} />;
}

function Contenido({
  fila,
  filas,
  informeId,
  ocupada,
  onGuardar,
  onQuitar,
  onCerrar,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
}: DialogoDePlantacionProps & { fila: PlantacionDeInforme }) {
  const inicial = useMemo(
    () => formularioDesdePlantacion(fila),
    // La fila llega de la caché y cambia de identidad con cada refetch (por
    // ejemplo, al subir una foto): lo que se escribe no se pisa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fila.id],
  );

  const [formulario, setFormulario] = useState<FormularioDePlantacion>(inicial);
  const [casilla, setCasilla] = useState(fila.validada);
  const [casillaTocada, setCasillaTocada] = useState(false);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);
  const [confirmandoQuitar, setConfirmandoQuitar] = useState(false);

  const cambio = !mismosCampos(formulario, inicial);
  const hayCambios = cambio || casillaTocada;

  /* LO VALIDADO NO SOBREVIVE A UN CAMBIO: si la fila estaba validada y se
     tocó un dato, vuelve a quedar sin validar — salvo que la persona haya
     vuelto a marcar la casilla en el modal, que es decir "ya lo revisé". */
  const vuelveASinValidar = fila.validada && cambio && !casillaTocada;
  const validadaAlGuardar = vuelveASinValidar ? false : casilla;

  // LAS SUGERENCIAS EXCLUYEN LA FILA QUE SE ESTÁ CORRIGIENDO: sin excluirla,
  // "¿ya existe esta especie?" daría siempre verdadero.
  const otras = useMemo(() => filas.filter((f) => f.id !== fila.id), [filas, fila.id]);

  const nombre = nombreDeLaPlantacion(fila);
  const cantidadDeFotos = fila.fotos?.length ?? 0;

  function escribir(cambios: Partial<FormularioDePlantacion>) {
    setFormulario((actual) => ({ ...actual, ...cambios }));
  }

  function pedirCierre() {
    if (hayCambios) setConfirmandoCierre(true);
    else onCerrar();
  }

  function guardar() {
    onGuardar(fila, { formulario, validada: validadaAlGuardar });
  }

  const comunes = { idPrefijo: "plantacion-edicion", formulario, ocupada, escribir };

  return (
    <>
      <Dialog open onOpenChange={(abierto) => !abierto && pedirCierre()}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[calc(100vh-3.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[55rem]"
        >
          <div className="border-divisor flex flex-none items-center gap-3 border-b-[1.5px] px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-ink-hint text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
                Editar plantación
              </div>
              <DialogTitle className="text-ink mt-1 truncate text-lg font-extrabold">
                {nombre}
              </DialogTitle>
              <DialogDescription className="sr-only">
                Los datos se guardan con «Guardar plantación». Las fotos de una plantación ya
                guardada se guardan apenas las sueltas.
              </DialogDescription>
            </div>

            {vuelveASinValidar ? (
              <span className="bg-warning-soft text-warning-soft-foreground rounded-md px-2.5 py-1 text-xs font-semibold">
                Al guardar cambios vuelve a quedar sin validar
              </span>
            ) : null}

            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Cerrar"
              title="Cerrar"
              onClick={pedirCierre}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>

          <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto px-5 py-4.5">
            <RotuloDeGrupo>Identificación</RotuloDeGrupo>
            <GrupoDeIdentificacion
              {...comunes}
              filas={otras}
              conNotaDeTerreno={inicial.nota_terreno.trim() !== ""}
            />

            <div className="bg-divisor-suave h-px" />
            <RotuloDeGrupo>Datos de la plantación</RotuloDeGrupo>
            <GrupoDeDatos {...comunes} />
            <PlantasTotales formulario={formulario} />

            <div className="bg-divisor-suave h-px" />
            <FotosDeLaFila
              titulo="Fotos"
              etiquetaDelSelector="Adjuntar fotos de la plantación"
              ocupada={ocupada}
              editando
              pendientes={[]}
              guardadas={fila.fotos ?? []}
              urlDe={(foto) => urlDeLaFotoDeLaFila("plantaciones", informeId, fila.id, foto.id, foto.hash)}
              alt={`Foto de ${nombre}`}
              error={rechazoDeFoto}
              // La fila YA existe: cada foto se va sola, sin esperar el botón
              // de abajo — las imágenes se guardan al soltarlas.
              onElegidas={(archivos) => archivos.forEach((a) => onSubirFoto(fila.id, a))}
              onRechazo={setRechazoDeFoto}
              onQuitarPendiente={() => {}}
              onQuitarGuardada={(fotoId) => onQuitarFoto(fila.id, fotoId)}
              onLeyenda={
                onLeyendaDeFoto
                  ? (fotoId, leyenda) => onLeyendaDeFoto(fila.id, fotoId, leyenda)
                  : undefined
              }
            />
          </div>

          <div className="border-divisor bg-card flex flex-none flex-wrap items-center gap-2.5 border-t-[1.5px] px-5 py-3">
            <label
              className={cn(
                "flex w-fit items-center gap-2 text-xs font-bold",
                casilla ? "text-success-soft-foreground" : "text-ink-body",
              )}
            >
              <Checkbox
                checked={casilla}
                disabled={ocupada}
                onCheckedChange={(v) => {
                  setCasilla(v === true);
                  setCasillaTocada(true);
                }}
              />
              Información validada
            </label>

            {/* QUITAR SE LLEVA LAS FOTOS Y NO SE DESHACE, así que pide un
                segundo clic. La confirmación es en línea, sin otro modal
                encima de este. */}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={ocupada}
              aria-label="Quitar esta plantación"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              onClick={() => {
                if (!confirmandoQuitar) {
                  setConfirmandoQuitar(true);

                  return;
                }

                onQuitar(fila);
              }}
            >
              {confirmandoQuitar ? "¿Seguro? Quitar" : "Quitar"}
            </Button>
            {confirmandoQuitar ? (
              <span className="text-ink-muted text-caption">
                Se va “{nombre}” junto con sus {cantidadDeFotos} foto{cantidadDeFotos === 1 ? "" : "s"}.
                No se puede deshacer.
              </span>
            ) : null}

            <div className="ml-auto flex gap-2.5">
              <Button type="button" variant="outline" onClick={pedirCierre} disabled={ocupada}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={guardar}
                disabled={ocupada || formulario.especie.trim() === ""}
              >
                {ocupada ? <Spinner /> : null}
                Guardar plantación
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DialogoCambiosSinGuardar
        abierto={confirmandoCierre}
        descripcion={
          "Vas a perder los cambios sin guardar en esta plantación. Las fotos que ya se " +
          "subieron no se deshacen: siguen en el informe aunque descartes el resto."
        }
        guardando={ocupada}
        onGuardarYSalir={() => {
          setConfirmandoCierre(false);
          guardar();
        }}
        onSalirSinGuardar={() => {
          setConfirmandoCierre(false);
          onCerrar();
        }}
        onCancelar={() => setConfirmandoCierre(false)}
      />
    </>
  );
}
